import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import BodyContainer from "../components/BodyContainer";
import BottomContainer from "../components/BottomContainer";
import Banner from "../components/Banner";
import Footer from "../components/Footer";
import FooterLinks from "../components/FooterLinks";
import bannerImage from "../assets/banner4.png";
import { Helmet } from "react-helmet-async";

import "../PracticeTests.css";
import "../Site.css";

const MAX_QUESTIONS = 100;

function getTotalAvailableQuestions(categories, selectedCategories) {
  return selectedCategories.reduce((total, cat) => {
    return total + (categories[cat]?.length || 0);
  }, 0);
}

function groupByCategory(questions) {
  const grouped = {};

  questions.forEach((q) => {
    const category = q.Category || "Other";
    if (!grouped[category]) grouped[category] = [];
    grouped[category].push(q);
  });

  return grouped;
}

function shuffle(arr) {
  const copy = [...arr];

  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }

  return copy;
}

function QuestionBank() {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_BASE_URL;
  const user = JSON.parse(localStorage.getItem("user"));

  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  const [categories, setCategories] = useState({});
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [questionCount, setQuestionCount] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [validationMessage, setValidationMessage] = useState("");

  const sortedCategoryEntries = Object.entries(categories).sort(([a], [b]) =>
    a.localeCompare(b)
  );

  const totalAvailable = getTotalAvailableQuestions(
    categories,
    selectedCategories
  );

  const maxAllowed = Math.min(totalAvailable, MAX_QUESTIONS);

  useEffect(() => {
    if (!user?.Id) {
      navigate("/login");
      return;
    }

    const fetchBanks = async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/users/${user.Id}/questionbanks`
        );

        if (!res.ok) throw new Error("Failed to fetch question banks");

      const data = await res.json();

      console.log("=== USER QUESTION BANKS RESPONSE ===");
      console.log(data);

      console.log("=== QUESTION BANKS ===");
      console.table(data.questionBanks || []);

      setBanks(data.questionBanks || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBanks();
  }, [API_URL, navigate, user?.Id]);

  const handleConfigureBank = async (bank) => {
    try {
      setSelectedBank(bank);
      setSelectedCategories([]);
      setValidationMessage("");
      setQuestionCount("");

      const res = await fetch(`${API_URL}/api/questionbanks/${bank.Id}`);

      if (!res.ok) throw new Error("Failed to load question bank");

      const data = await res.json();

      setCategories(groupByCategory(data.Questions || []));
    } catch (err) {
      setError(err.message);
    }
  };

  const validateQuestionCount = (count, selectedCats) => {
    const available = getTotalAvailableQuestions(categories, selectedCats);
    const allowed = Math.min(available, MAX_QUESTIONS);

    if (!count) {
      setValidationMessage("");
      return;
    }

    if (Number(count) > MAX_QUESTIONS) {
      setValidationMessage(`The maximum question bank size is ${MAX_QUESTIONS} questions.`);
      return;
    }

    if (Number(count) > available) {
      setValidationMessage(
        `Only ${available} questions are available in the selected categories.`
      );
      return;
    }

    if (Number(count) > allowed) {
      setValidationMessage(`You can select up to ${allowed} questions.`);
      return;
    }

    setValidationMessage("");
  };

  const toggleCategory = (cat) => {
    setSelectedCategories((prev) => {
      const updated = prev.includes(cat)
        ? prev.filter((c) => c !== cat)
        : [...prev, cat];

      validateQuestionCount(questionCount, updated);

      return updated;
    });
  };

  const handleSelectAllCategories = () => {
    const allCategories = sortedCategoryEntries.map(([cat]) => cat);
    setSelectedCategories(allCategories);
    validateQuestionCount(questionCount, allCategories);
  };

  const handleStartPractice = () => {
    const count = Number(questionCount);

    if (!selectedCategories.length) {
      setValidationMessage("Please select at least one category.");
      return;
    }

    if (!count || count <= 0) {
      setValidationMessage("Enter a valid number of questions.");
      return;
    }

    if (!questionCount || questionCount.trim() === "") {
      setValidationMessage(
    "Please type in your desired number of test questions.");
      return;
    }

    if (count > MAX_QUESTIONS) {
      setValidationMessage(`The maximum question bank size is ${MAX_QUESTIONS} questions.`);
      return;
    }

    if (count > totalAvailable) {
      setValidationMessage(
        `Only ${totalAvailable} questions are available in the selected categories.`
      );
      return;
    }

    setValidationMessage("");

    let selected = [];
    const pools = {};

    selectedCategories.forEach((cat) => {
      pools[cat] = shuffle(categories[cat] || []);
    });

    const perCat = Math.floor(count / selectedCategories.length);
    let remaining = count;

    selectedCategories.forEach((cat) => {
      const pool = pools[cat];
      const take = Math.min(perCat, pool.length);

      selected.push(...pool.splice(0, take));
      remaining -= take;
    });

    while (remaining > 0) {
      let added = false;

      for (const cat of selectedCategories) {
        if (pools[cat]?.length > 0 && remaining > 0) {
          selected.push(pools[cat].shift());
          remaining--;
          added = true;
        }
      }

      if (!added) break;
    }

    selected = shuffle(selected);

    navigate(`/practicequestionsPage/${selectedBank.Id}`, {
      state: {
        questions: selected,
        questionBank: selectedBank,
      },
    });
  };

  return (
    <>
      <Helmet>
        <title>PharmPrepPro - Question Banks</title>
      </Helmet>

      <BottomContainer>
        <Banner height="300px" image={bannerImage} />

        <BodyContainer>
          <h1 className="test-header">
            {selectedBank ? selectedBank.Name : "Available Practice Questions"}
          </h1>

          {!selectedBank && (
            <>
              {loading ? (
                <p className="no-tests-message">Loading practice questions...</p>
              ) : error ? (
                <p className="error-message">{error}</p>
              ) : (
                <ul className="test-list">
                  {banks.map((bank) => (
                    <li key={bank.Id} className="test-item">
                      <span className="test-name">{bank.Name}</span>
                      <button
                        className="start-button"
                        onClick={() => handleConfigureBank(bank)}
                      >
                        Configure
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

          {selectedBank && (
            <>
              <h3 style={{ color: "white" }}>Select Categories</h3>

              <ul className="test-list">
                {sortedCategoryEntries.map(([cat, list]) => (
                  <li key={cat} className="test-item">
                    <label className="test-name">
                      <input
                        type="checkbox"
                        checked={selectedCategories.includes(cat)}
                        onChange={() => toggleCategory(cat)}
                        style={{ marginRight: "10px" }}
                      />
                      {cat} ({list.length})
                    </label>
                  </li>
                ))}
              </ul>

              <div style={{ textAlign: "center", marginTop: "15px" }}>
                <button className="start-button" onClick={handleSelectAllCategories}>
                  Select All Categories
                </button>
              </div>

              <h3 style={{ color: "white", textAlign: "center" }}>
                Number of Questions
              </h3>

              {selectedCategories.length > 0 && (
                <p style={{ color: "white", textAlign: "center" }}>
                  Available: {totalAvailable} | Max Allowed: {maxAllowed}
                </p>
              )}

              {validationMessage && (
                <p style={{ color: "red", textAlign: "center" }}>
                  {validationMessage}
                </p>
              )}

              <div style={{ textAlign: "center" }}>
                <input
                  type="text"
                  value={questionCount}
                  placeholder="Enter number of questions"
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");

                    if (Number(value) > MAX_QUESTIONS) {
                      setQuestionCount(value);
                      setValidationMessage(
                        `The maximum test size is ${MAX_QUESTIONS} questions.`
                      );
                      return;
                    }

                    setQuestionCount(value);
                    validateQuestionCount(value, selectedCategories);
                  }}
                  style={{
                    padding: "10px",
                    fontSize: "16px",
                    width: "220px",
                    textAlign: "center",
                  }}
                />
              </div>

              <div
                style={{
                  marginTop: "20px",
                  display: "flex",
                  justifyContent: "center",
                  gap: "10px",
                }}
              >
                <button
                  className="start-button"
                  disabled={
                    !selectedCategories.length ||
                    !questionCount ||
                    Number(questionCount) <= 0 ||
                    Number(questionCount) > MAX_QUESTIONS ||
                    Number(questionCount) > totalAvailable
                  }
                  onClick={handleStartPractice}
                >
                  Start Practice
                </button>

                <button
                  className="start-button"
                  onClick={() => {
                    setSelectedBank(null);
                    setCategories({});
                    setSelectedCategories([]);
                    setQuestionCount("");
                    setValidationMessage("");
                  }}
                >
                  Back
                </button>
              </div>
            </>
          )}
        </BodyContainer>
      </BottomContainer>

      <FooterLinks />
      <Footer />
    </>
  );
}

export default QuestionBank;